import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { payrollAPI } from '../../../../services/api';

const OFFICIAL_RATES = {
  pmss: 3864,
  csg_non_deductible_rate: 2.90,
  csg_deductible_rate: 6.80,
  crds_rate: 0.50,
  retraite_t1_employee: 3.15,
  retraite_t2_employee: 8.64,
  pas_mode: 'neutre',
  employee_tax_rate: 0,
};

const RateField = ({ label, value, onChange, disabled, suffix = '%' }) => (
  <div className="space-y-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <div className="flex items-center gap-1">
      <Input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        disabled={disabled}
        className="h-8 text-sm"
      />
      <span className="text-xs text-muted-foreground w-6">{suffix}</span>
    </div>
  </div>
);

const FrancePayrollForm = () => {
  const { t } = useTranslation();
  const [rates, setRates] = useState(OFFICIAL_RATES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await payrollAPI.getPayrollSettings();
        if (data?.france) setRates((prev) => ({ ...prev, ...data.france }));
      } catch {
        // fallback to official rates
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const updateRate = (key, val) => setRates((prev) => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    try {
      setSaving(true);
      await payrollAPI.updatePayrollSettings({ france: rates });
      toast.success(t('payrollFR.saveSuccess'));
    } catch {
      toast.error(t('payrollCfg.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = () => {
    setRates(OFFICIAL_RATES);
    toast.info(t('payrollCfg.restored'));
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">🇫🇷 {t('payrollFR.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('payrollFR.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRestore}>
            <RotateCcw className="h-4 w-4 mr-1" /> {t('payrollCfg.restore')}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-1" /> {saving ? t('payrollCfg.saving') : t('payrollCfg.save')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('payrollFR.pmssCard')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <RateField label={t('payrollFR.pmssLabel')} value={rates.pmss} onChange={(v) => updateRate('pmss', v)} suffix="€" />
            <p className="text-xs text-muted-foreground">{t('payrollFR.csgBaseNote')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('payrollFR.csgCard')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <RateField label={t('payrollFR.csgNonDed')} value={rates.csg_non_deductible_rate} onChange={(v) => updateRate('csg_non_deductible_rate', v)} />
            <RateField label={t('payrollFR.csgDed')} value={rates.csg_deductible_rate} onChange={(v) => updateRate('csg_deductible_rate', v)} />
            <RateField label={t('payrollFR.crds')} value={rates.crds_rate} onChange={(v) => updateRate('crds_rate', v)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('payrollFR.retraiteCard')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <RateField label={t('payrollFR.t1')} value={rates.retraite_t1_employee} onChange={(v) => updateRate('retraite_t1_employee', v)} />
            <RateField label={t('payrollFR.t2')} value={rates.retraite_t2_employee} onChange={(v) => updateRate('retraite_t2_employee', v)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('payrollFR.pasCard')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('payrollFR.pasMode')}</Label>
              <Select value={rates.pas_mode} onValueChange={(v) => updateRate('pas_mode', v)}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="neutre">{t('payrollFR.pasNeutral')}</SelectItem>
                  <SelectItem value="individuel">{t('payrollFR.pasIndiv')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {rates.pas_mode === 'individuel' && (
              <RateField
                label={t('payrollFR.pasRate')}
                value={rates.employee_tax_rate}
                onChange={(v) => updateRate('employee_tax_rate', v)}
              />
            )}
            {rates.pas_mode === 'neutre' && (
              <p className="text-xs text-muted-foreground">{t('payrollFR.pasNeutralNote')}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FrancePayrollForm;
